import { ApplicationRef, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { environment } from 'environments/environment';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppComponent } from 'app/app.component';
import { FUSE_APP_CONFIG } from '@fuse/services/config/config.constants';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { ExtraOptions, PreloadAllModules, RouterModule } from '@angular/router';
import { appRoutes } from 'app/app.routing';
import { FuseModule } from '@fuse';
import { FuseConfigModule } from '@fuse/services/config';
import { appConfig, MSALInstanceFactory } from 'app/core/config/app.config';
import { mockApiServices } from 'app/mock-api';
import { FuseMockApiModule } from '@fuse/lib/mock-api';
import { CoreModule } from 'app/core/core.module';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { LayoutModule } from 'app/layout/layout.module';
import { CamelCaseToWordsPipe } from 'app/shared/pipes/camel-case-to-words.pipe';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { CtrlClickDirective } from 'app/shared/directives/ctrl-click.directive';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { DataFormModule } from 'app/shared/components/data-form/data-form.module';
import { NgxMaskModule } from 'ngx-mask';

const fuseAppConfig = {
    theme: 'brand',
    scheme: 'light',
    breakpoints: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920
    },
    screens: {
        print: { 'raw': 'print' },
        sm: '375px',
        md: '768px',
        lg: '1024px',
        xl: '1440px'
    },
    // your configuration values here
};

const routerConfig: ExtraOptions = {
    scrollPositionRestoration: 'enabled',
    preloadingStrategy: PreloadAllModules,
    onSameUrlNavigation: 'reload',
    // v22 changed the default to 'always'; pinned to preserve pre-upgrade route param inheritance.
    paramsInheritanceStrategy: 'emptyOnly'
};

if (environment.production) {
    enableProdMode();
}

const bootstrap = (): Promise<ApplicationRef> => bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),{
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        MsalService,
        provideAnimations(),
        importProvidersFrom(
            BrowserModule,
            BrowserAnimationsModule,
            RouterModule.forRoot(appRoutes, routerConfig),
            // Fuse, FuseConfig & FuseMockAPI
            FuseModule,
            FuseConfigModule.forRoot(appConfig),
            FuseMockApiModule.forRoot(mockApiServices),
            // Core module of your application
            CoreModule,
            // Layout module of your application
            LayoutModule,
            // 3rd party modules that require global configuration via forRoot
            MatNativeDateModule,
            MatSidenavModule,
            MatIconModule,
            // Data form module for app-level drawer form support
            DataFormModule,
            NgxMaskModule.forRoot(),
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: environment.production,
                registrationStrategy: 'registerWhenStable:30000'
            }),
        ),
        CamelCaseToWordsPipe,
        DatePipe,
        DecimalPipe,
        CtrlClickDirective,
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'fill' },
        },
        { provide: FUSE_APP_CONFIG, useValue: fuseAppConfig } // Provide the config here
    ],
});

if (window.location.pathname.endsWith('/auth-callback')) {
    broadcastResponseToMainFrame().catch((error: Error) => console.error(error));
} else {
    bootstrap().catch((error: Error) => console.error(error));
}
